import React, { useEffect, useState } from "react";
import { db, auth } from "../FirebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import {
  Folder,
  User,
  PlusCircle,
  Calendar,
  Clock,
  DollarSign,
  FileText,
  Globe,
  Mail,
  Phone,
  Link as LinkIcon,
  CheckCircle,
  XCircle,
} from "lucide-react";
import Footer from "./Footer";
import Header from "./Header";

const ClientDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [freelancers, setFreelancers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userUid, setUserUid] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    console.log("Initial localStorage user:", storedUser);
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUserRole(parsedUser.role || "client");
      setUserUid(parsedUser.uid || null);
      console.log("Initial userRole:", parsedUser.role || "client");
      console.log("Initial userUid:", parsedUser.uid || null);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log("Firebase auth user:", user);
      if (user) {
        setCurrentUserEmail(user.email);
        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        setUserRole(storedUser.role || "client");
        setUserUid(storedUser.uid || user.uid);
        console.log("Firebase sync - userRole:", storedUser.role || "client");
        console.log("Firebase sync - userUid:", storedUser.uid || user.uid);
      } else {
        setCurrentUserEmail(null);
        setUserRole(null);
        setUserUid(null);
        console.log("No user authenticated, redirecting to /");
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUserEmail || !userUid || !userRole) {
        console.log("Skipping fetch: missing email, uid, or role", {
          currentUserEmail,
          userUid,
          userRole,
        });
        setLoading(false);
        return;
      }
      try {
        if (userRole === "mentor") {
          console.log("Fetching courses for mentor with uid:", userUid);
          const coursesQuery = query(
            collection(db, "courses"),
            where("uid", "==", userUid)
          );
          const coursesSnapshot = await getDocs(coursesQuery);
          const coursesList = coursesSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          console.log("Fetched courses:", coursesList);
          setCourses(coursesList);
        } else if (userRole === "freelancer") {
          console.log("Fetching freelancer data with uid:", userUid);
          const freelancerQuery = query(
            collection(db, "freelancer"),
            where("uid", "==", userUid)
          );
          const freelancerSnapshot = await getDocs(freelancerQuery);
          const freelancerList = freelancerSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          console.log("Fetched freelancers:", freelancerList);
          setFreelancers(freelancerList);
        } else {
          console.log(
            "Fetching projects for client with email:",
            currentUserEmail
          );
          const projectsQuery = query(
            collection(db, "projects"),
            where("email", "==", currentUserEmail)
          );
          const projectsSnapshot = await getDocs(projectsQuery);
          const projectsList = projectsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          console.log("Fetched projects:", projectsList);
          setProjects(projectsList);
        }
      } catch (error) {
        console.error(
          `Error fetching ${
            userRole === "mentor"
              ? "courses"
              : userRole === "freelancer"
              ? "freelancer data"
              : "projects"
          }:`,
          error
        );
        toast.error(
          `Failed to load ${
            userRole === "mentor"
              ? "courses"
              : userRole === "freelancer"
              ? "freelancer data"
              : "projects"
          }. Please try again.`
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentUserEmail, userRole, userUid]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp || !timestamp.seconds) return "N/A";
    return format(new Date(timestamp.seconds * 1000), "MMMM d, yyyy");
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "submitted request":
      case "pending":
        return "bg-red-100 text-red-800";
      case "approved":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-4 border-t-orange-500 border-gray-200 rounded-full"
        ></motion.div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-6xl mx-auto px-4 lg:px-10 py-8 md:py-16 bg-white shadow-xl rounded-2xl my-8"
      >
        <div className="flex justify-between">
          <div className="mb-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">
              {userRole === "mentor"
                ? "Mentor Dashboard"
                : userRole === "freelancer"
                ? "Freelancer Dashboard"
                : "Client Dashboard"}
            </h2>
            <div className="border-t-4 border-orange-500 mt-3 w-16 rounded-full"></div>
          </div>

          <div className="mb-8 text-center">
            <Link
              to={
                userRole === "mentor"
                  ? "/mentor-form"
                  : userRole === "freelancer"
                  ? "/freelancer-form"
                  : "/project-form"
              }
              className="inline-flex items-center bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-8 rounded-full font-semibold shadow-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300 transform hover:scale-105"
            >
              <PlusCircle className="w-5 h-5 mr-2" />
              {userRole === "mentor"
                ? "Add a Course"
                : userRole === "freelancer"
                ? "Update Profile"
                : "Start a New Project"}
            </Link>
          </div>
        </div>
        <div className="mb-8">
          <h3 className="text-2xl font-semibold text-gray-900 mb-6">
            {userRole === "mentor"
              ? "Your Courses"
              : userRole === "freelancer"
              ? "Your Profile"
              : "Your Projects"}
          </h3>

          <AnimatePresence>
            {userRole === "mentor" ? (
              courses.length ? (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {courses.map((course) => (
                    <motion.div
                      key={course.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.3 }}
                      className="p-6 border rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 bg-white"
                    >
                      <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                        <Folder className="w-6 h-6 mr-2 text-orange-500" />
                        {course.coursename}
                      </h4>
                      <div className="space-y-3 text-sm text-gray-700">
                        <p className="flex items-start">
                          <FileText className="w-5 h-5 mr-2 text-gray-500 flex-shrink-0 mt-1" />
                          <span>
                            <strong>Description:</strong> {course.discription}
                          </span>
                        </p>
                        <p className="flex items-center">
                          <Clock className="w-5 h-5 mr-2 text-gray-500" />
                          <span>
                            <strong>Duration:</strong> {course.duration}
                          </span>
                        </p>
                        <p className="flex items-center">
                          <DollarSign className="w-5 h-5 mr-2 text-gray-500" />
                          <span>
                            <strong>Charges:</strong> {course.charges}
                          </span>
                        </p>
                        <p className="flex items-center">
                          <Calendar className="w-5 h-5 mr-2 text-gray-500" />
                          <span>
                            <strong>Start Date:</strong> {course.starting}
                          </span>
                        </p>
                        <p className="flex items-center">
                          <Calendar className="w-5 h-5 mr-2 text-gray-500" />
                          <span>
                            <strong>End Date:</strong> {course.endingdate}
                          </span>
                        </p>
                        <p className="flex items-center">
                          <Globe className="w-5 h-5 mr-2 text-gray-500" />
                          <span>
                            <strong>Lecture Mode:</strong> {course.lectureMode}
                          </span>
                        </p>
                        <p className="flex items-center">
                          <Clock className="w-5 h-5 mr-2 text-gray-500" />
                          <span>
                            <strong>Lecture Time:</strong> {course.lectureTime}
                          </span>
                        </p>
                        <p className="flex items-center">
                          <Calendar className="w-5 h-5 mr-2 text-gray-500" />
                          <span>
                            <strong>Weekdays:</strong>{" "}
                            {course.weekdays?.join(", ") || "N/A"}
                          </span>
                        </p>
                        <p className="flex items-center">
                          <CheckCircle className="w-5 h-5 mr-2 text-gray-500" />
                          <span>
                            <strong>Status:</strong>{" "}
                            <span
                              className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusClass(
                                course.status
                              )}`}
                            >
                              {course.status}
                            </span>
                          </span>
                        </p>
                      </div>
                      {course.pictureUrl && (
                        <motion.img
                          src={course.pictureUrl}
                          alt={course.coursename}
                          className="mt-4 w-full h-48 object-cover rounded-lg"
                          whileHover={{ scale: 1.05 }}
                          transition={{ duration: 0.3 }}
                        />
                      )}
                    </motion.div>
                  ))}
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="text-center py-12 bg-gray-50 rounded-xl border"
                >
                  <p className="text-gray-600 mb-4">
                    No courses found. Add a course to get started!
                  </p>
                  <Link
                    to="/mentor-form"
                    className="inline-flex items-center bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-8 rounded-full font-semibold shadow-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300"
                  >
                    <PlusCircle className="w-5 h-5 mr-2" />
                    Add a Course
                  </Link>
                </motion.div>
              )
            ) : userRole === "freelancer" ? (
              freelancers.length ? (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {freelancers.map((freelancer) => (
                    <motion.div
                      key={freelancer.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.3 }}
                      className="p-6 border rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 bg-white"
                    >
                      <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                        <User className="w-6 h-6 mr-2 text-orange-500" />
                        {freelancer.name}
                      </h4>
                      <div className="space-y-3 text-sm text-gray-700">
                        <p className="flex items-start">
                          <FileText className="w-5 h-5 mr-2 text-gray-500 flex-shrink-0 mt-1" />
                          <span>
                            <strong>Expertise:</strong> {freelancer.expertise}
                          </span>
                        </p>
                        <p className="flex items-center">
                          <Globe className="w-5 h-5 mr-2 text-gray-500" />
                          <span>
                            <strong>Region:</strong> {freelancer.region}
                          </span>
                        </p>
                        <p className="flex items-start">
                          <FileText className="w-5 h-5 mr-2 text-gray-500 flex-shrink-0 mt-1" />
                          <span>
                            <strong>About:</strong> {freelancer.about}
                          </span>
                        </p>
                        <p className="flex items-center">
                          <CheckCircle className="w-5 h-5 mr-2 text-gray-500" />
                          <span>
                            <strong>Skills:</strong>{" "}
                            {freelancer.skills?.join(", ") || "N/A"}
                          </span>
                        </p>
                        <p className="flex items-center">
                          <Mail className="w-5 h-5 mr-2 text-gray-500" />
                          <span>
                            <strong>Email:</strong> {freelancer.contact.email}
                          </span>
                        </p>
                        <p className="flex items-center">
                          <Phone className="w-5 h-5 mr-2 text-gray-500" />
                          <span>
                            <strong>Phone:</strong> {freelancer.contact.phone}
                          </span>
                        </p>
                        <p className="flex items-center">
                          <LinkIcon className="w-5 h-5 mr-2 text-gray-500" />
                          <span>
                            <strong>Portfolio:</strong>{" "}
                            {freelancer.portfolio ? (
                              <a
                                href={freelancer.portfolio}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-orange-500 hover:underline"
                              >
                                View Portfolio
                              </a>
                            ) : (
                              "N/A"
                            )}
                          </span>
                        </p>
                        <p className="flex items-center">
                          <LinkIcon className="w-5 h-5 mr-2 text-gray-500" />
                          <span>
                            <strong>Social Links:</strong>{" "}
                            {Object.entries(freelancer.socialLinks).map(
                              ([platform, url]) =>
                                url ? (
                                  <a
                                    key={platform}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-orange-500 hover:underline mr-2"
                                  >
                                    {platform.charAt(0).toUpperCase() +
                                      platform.slice(1)}
                                  </a>
                                ) : null
                            )}
                          </span>
                        </p>
                        <p className="flex items-center">
                          <CheckCircle className="w-5 h-5 mr-2 text-gray-500" />
                          <span>
                            <strong>Status:</strong>{" "}
                            <span
                              className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusClass(
                                freelancer.status
                              )}`}
                            >
                              {freelancer.status}
                            </span>
                          </span>
                        </p>
                        <p className="flex items-center">
                          {freelancer.applyForInterview ? (
                            <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
                          ) : (
                            <XCircle className="w-5 h-5 mr-2 text-red-500" />
                          )}
                          <span>
                            <strong>Apply for Interview:</strong>{" "}
                            {freelancer.applyForInterview ? "Yes" : "No"}
                          </span>
                        </p>
                      </div>
                      {freelancer.pictureUrl && (
                        <motion.img
                          src={freelancer.pictureUrl}
                          alt={freelancer.name}
                          className="mt-4 w-full h-48 object-cover rounded-lg"
                          whileHover={{ scale: 1.05 }}
                          transition={{ duration: 0.3 }}
                        />
                      )}
                    </motion.div>
                  ))}
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="text-center py-12 bg-gray-50 rounded-xl border"
                >
                  <p className="text-gray-600 mb-4">
                    No freelancer profile found. Update your profile to get
                    started!
                  </p>
                  <Link
                    to="/freelancer-form"
                    className="inline-flex items-center bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-8 rounded-full font-semibold shadow-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300"
                  >
                    <PlusCircle className="w-5 h-5 mr-2" />
                    Update Profile
                  </Link>
                </motion.div>
              )
            ) : projects.length > 0 ? (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {projects.map((project) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                    className="p-6 border rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 bg-white"
                  >
                    <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                      <Folder className="w-6 h-6 mr-2 text-orange-500" />
                      {project.projectName}
                    </h4>
                    <div className="space-y-3 text-sm text-gray-700">
                      <p className="flex items-start">
                        <FileText className="w-5 h-5 mr-2 text-gray-500 flex-shrink-0 mt-1" />
                        <span>
                          <strong>Details:</strong>{" "}
                          {project.detailedRequirement}
                        </span>
                      </p>
                      <p className="flex items-center">
                        <Clock className="w-5 h-5 mr-2 text-gray-500" />
                        <span>
                          <strong>Duration:</strong>{" "}
                          {project.durationDays || project.durationMonths
                            ? `${project.durationDays || ""}${
                                project.durationDays ? " Days" : ""
                              }${
                                project.durationMonths
                                  ? `${project.durationDays ? ", " : ""}${
                                      project.durationMonths
                                    } Months`
                                  : ""
                              }`
                            : "N/A"}
                        </span>
                      </p>
                      <p className="flex items-center">
                        <Mail className="w-5 h-5 mr-2 text-gray-500" />
                        <span>
                          <strong>Email:</strong> {project.email}
                        </span>
                      </p>
                      <p className="flex items-center">
                        <DollarSign className="w-5 h-5 mr-2 text-gray-500" />
                        <span>
                          <strong>Estimated Budget:</strong>{" "}
                          {project.estimatedBudget}
                        </span>
                      </p>
                      <p className="flex items-start">
                        <FileText className="w-5 h-5 mr-2 text-gray-500 flex-shrink-0 mt-1" />
                        <span>
                          <strong>Feedback:</strong>{" "}
                          {project.feedback || "None"}
                        </span>
                      </p>
                      <p className="flex items-center">
                        <Calendar className="w-5 h-5 mr-2 text-gray-500" />
                        <span>
                          <strong>Created At:</strong>{" "}
                          {formatTimestamp(project.createdAt)}
                        </span>
                      </p>
                      <p className="flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2 text-gray-500" />
                        <span>
                          <strong>Status:</strong>{" "}
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusClass(
                              project.status
                            )}`}
                          >
                            {project.status}
                          </span>
                        </span>
                      </p>
                      <p className="flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2 text-gray-500" />
                        <span>
                          <strong>Payment Status:</strong>{" "}
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusClass(
                              project.paymentStatus
                            )}`}
                          >
                            {project.paymentStatus}
                          </span>
                        </span>
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="text-center py-12 bg-gray-50 rounded-xl border"
              >
                <p className="text-gray-600 mb-4">
                  No projects found. Start a new project to get started!
                </p>
                {/* <Link
                  to="/project-form"
                  className="inline-flex items-center bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-8 rounded-full font-semibold shadow-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300"
                >
                  <PlusCircle className="w-5 h-5 mr-2" />
                  Start a New Project
                </Link> */}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
      <Footer />
    </>
  );
};

export default ClientDashboard;
